// =============================================================================
// SCHEDULE CALL FORM COMPONENT
// =============================================================================
// Contact form variation for scheduling calls with time slot selection

import { useState, useEffect } from 'react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input/Input';
import { availabilityService, type TimeSlot, type BookingRequest } from '@/services/availabilityService';
import { useToastNotifications } from '@/hooks/useToastNotifications';
import { COMMON_TIMEZONES, getBrowserTimezone } from '@/utils/timezones';
import './ScheduleCallForm.scss';

interface ScheduleCallFormProps {
  onSuccess?: () => void;
  className?: string;
}

export function ScheduleCallForm({ onSuccess, className = '' }: ScheduleCallFormProps) {
  const toast = useToastNotifications();
  
  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    notes: '',
    meetingType: 'consultation'
  });

  // Calendar state
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(getBrowserTimezone());

  // Load available slots when date or timezone changes
  useEffect(() => {
    loadAvailableSlots();
  }, [selectedDate, selectedTimezone]);

  const loadAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      const response = await availabilityService.getAvailableSlots(startDate, endDate, selectedTimezone);
      
      if (response.success && response.timeSlots) {
        setAvailableSlots(response.timeSlots);
      } else {
        toast.showError(response.error || 'Failed to load available time slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error loading available slots:', error);
      toast.showError('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const handleTimeSlotSelect = (slot: TimeSlot) => {
    if (slot.available) {
      setSelectedTimeSlot(slot);
    }
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: selectedTimezone
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: selectedTimezone
    });
  };

  const getAvailableSlotsForDate = (): TimeSlot[] => {
    return availableSlots.filter(slot => slot.available);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTimeSlot) {
      toast.showError('Please select a time slot');
      return;
    }

    if (!formData.clientName || !formData.clientEmail) {
      toast.showError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const bookingRequest: BookingRequest = {
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone,
        startTime: selectedTimeSlot.start,
        endTime: selectedTimeSlot.end,
        timezone: selectedTimeSlot.timezone,
        notes: formData.notes,
        meetingType: formData.meetingType
      };

      const response = await availabilityService.bookTimeSlot(bookingRequest);

      if (response.success) {
        toast.showSuccess('Call scheduled successfully! You will receive a confirmation email shortly.');
        
        // Reset form
        setFormData({
          clientName: '',
          clientEmail: '',
          clientPhone: '',
          notes: '',
          meetingType: 'consultation'
        });
        setSelectedTimeSlot(null);
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.showError(response.error || 'Failed to schedule call');
      }
    } catch (error) {
      console.error('Error scheduling call:', error);
      toast.showError('An error occurred while scheduling your call');
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    handleDateChange(newDate);
  };

  return (
    <div className={`schedule-call-form ${className}`}>
      <div className="schedule-call-header">
        <h3>Schedule a Call</h3>
        <p>Choose a convenient time slot for your consultation call.</p>
      </div>

      <form onSubmit={handleSubmit} className="schedule-call-form-content">
        {/* Client Information */}
        <div className="form-section">
          <h4>Your Information</h4>
          <div className="form-grid">
            <div className="form-field">
              <Input
                label="Name *"
                value={formData.clientName}
                onChange={(value) => handleInputChange('clientName', value)}
                placeholder="Your full name"
                required
              />
            </div>
            
            <div className="form-field">
              <Input
                label="Email *"
                type="email"
                value={formData.clientEmail}
                onChange={(value) => handleInputChange('clientEmail', value)}
                placeholder="your.email@example.com"
                required
              />
            </div>
            
            <div className="form-field">
              <Input
                label="Phone (Optional)"
                type="tel"
                value={formData.clientPhone}
                onChange={(value) => handleInputChange('clientPhone', value)}
                placeholder="(555) 123-4567"
              />
            </div>
            
            <div className="form-field">
              <label htmlFor="meetingType">Meeting Type</label>
              <select
                id="meetingType"
                value={formData.meetingType}
                onChange={(e) => handleInputChange('meetingType', e.target.value)}
                className="form-select"
              >
                <option value="consultation">Consultation</option>
                <option value="strategy">Strategy Session</option>
                <option value="follow-up">Follow-up Call</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Date Selection */}
        <div className="form-section">
          <div className="section-header-with-timezone">
            <h4>Select Date & Time</h4>
            <div className="timezone-selector">
              <label htmlFor="timezone">Timezone:</label>
              <select
                id="timezone"
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="form-select"
              >
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="date-navigation">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigateDate('prev')}
              disabled={loadingSlots}
            >
              ← Previous
            </Button>
            
            <div className="selected-date">
              {formatDate(selectedDate)}
            </div>
            
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigateDate('next')}
              disabled={loadingSlots}
            >
              Next →
            </Button>
          </div>

          {/* Time Slots */}
          <div className="time-slots-container">
            {loadingSlots ? (
              <div className="loading-slots">
                <div className="loading-spinner"></div>
                <p>Loading available time slots...</p>
              </div>
            ) : (
              <div className="time-slots-grid">
                {getAvailableSlotsForDate().length === 0 ? (
                  <div className="no-slots">
                    <p>No available time slots for this date.</p>
                    <p>Please try a different date.</p>
                  </div>
                ) : (
                  getAvailableSlotsForDate().map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`time-slot ${selectedTimeSlot?.start === slot.start ? 'selected' : ''}`}
                      onClick={() => handleTimeSlotSelect(slot)}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedTimeSlot && (
            <div className="selected-slot-info">
              <p>
                <strong>Selected:</strong> {formatDate(selectedDate)} at {formatTime(selectedTimeSlot.start)}
              </p>
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="form-section">
          <h4>Additional Information</h4>
          <div className="form-field">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Tell us about your project or any specific topics you'd like to discuss..."
              className="form-textarea"
              rows={4}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !selectedTimeSlot}
            className="schedule-button"
          >
            {loading ? 'Scheduling...' : 'Schedule Call'}
          </Button>
        </div>
      </form>
    </div>
  );
}
